import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import IncomeStatementServ, { BranchScope, IncomeStatementData } from '@api/IncomeStatement/IncomeStatementServ';
import {
    buildIncomeStatement,
    buildIncomeStatementByBranch,
    buildPreviousRange,
    buildStatementRange,
    IncomeStatement,
    STATEMENT_ROWS,
    StatementRow,
    variation
} from '@utils/IncomeStatement';
import { formatPeriod, periodOf } from '@utils/Expenses';
import { currencyFormatter } from '@utils/Numbers';
import { canCurrentUser } from '@utils/Permissions';
import { Capability, seesAllBranches } from '@utils/Permissions/Capabilities';
import { usePermissionSubject, useVisibleBranchOffices } from '@hooks/usePermissionSubject';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';

interface IncomeStatementPanelProps {
    /** Sucursal del filtro del Informe (`'all'` = consolidado, solo el admin general). */
    branchOfficeId: string;
}

const MONTHS_BACK = 18;
const percent = (value: number | null): string => (value === null ? '—' : `${(value * 100).toFixed(1)}%`);

const signOf = (row: StatementRow): string => (row.kind === 'minus' ? '−' : row.kind === 'plus' ? '+' : row.kind === 'result' ? '=' : '');

/**
 * Estado de Resultados (plan de Gastos §5.3, F5): por mes (o rango de meses), por
 * sucursal y consolidado, contra el mismo periodo anterior, **a día cerrado** (hoy no
 * entra). Las ventas salen de `daily-sales` (lo escribe el aseo), no de los pedidos.
 * Lecturas: ver `IncomeStatementServ`.
 */
const IncomeStatementPanel: React.FunctionComponent<IncomeStatementPanelProps> = ({ branchOfficeId }) => {
    const branchOffices = useVisibleBranchOffices();
    const subject = usePermissionSubject();
    const monthOptions = useMemo(() => Array.from({ length: MONTHS_BACK }, (_, index) => periodOf(new Date(), -index)), []);
    const [from, setFrom] = useState(monthOptions[0]);
    const [to, setTo] = useState(monthOptions[0]);
    const [data, setData] = useState<IncomeStatementData | undefined>(undefined);

    const consolidated = branchOfficeId === 'all';
    const scope: BranchScope = useMemo(() => {
        if (!consolidated) return [branchOfficeId];
        if (canCurrentUser(Capability.IncomeStatementConsolidated) && seesAllBranches(subject)) return 'all';
        return branchOffices.map((branch) => branch.id!).filter((id) => canCurrentUser(Capability.IncomeStatementView, { branchOfficeId: id }));
    }, [branchOfficeId, consolidated, branchOffices, subject]);

    const [start, end] = from <= to ? [from, to] : [to, from];
    const range = useMemo(() => buildStatementRange(start, end), [start, end]);
    const previousRange = useMemo(() => buildPreviousRange(range, start, end), [range, start, end]);

    useEffect(() => {
        let alive = true;
        showLoader();
        void IncomeStatementServ.getIncomeStatementData([range, previousRange], scope)
            .then((result) => alive && setData(result))
            .catch(showAlertError)
            .finally(hideLoader);
        return () => {
            alive = false;
        };
    }, [range, previousRange, scope]);

    const current = useMemo(() => (data ? buildIncomeStatement({ ...data, range }) : undefined), [data, range]);
    const previous = useMemo(() => (data ? buildIncomeStatement({ ...data, range: previousRange }) : undefined), [data, previousRange]);
    const branchIds = useMemo(
        () => (scope === 'all' ? branchOffices.map((branch) => branch.id!) : scope),
        [scope, branchOffices]
    );
    const byBranch = useMemo(
        () => (data && branchIds.length > 1 ? buildIncomeStatementByBranch({ ...data, range }, branchIds) : undefined),
        [data, range, branchIds]
    );
    const branchName = (id: string) => branchOffices.find((branch) => branch.id === id)?.name || id;
    const periodLabel = start === end ? formatPeriod(start) : `${formatPeriod(start)} – ${formatPeriod(end)}`;
    const expectedDays = useMemo(() => {
        if (range.empty) return 0;
        const days = Math.round((new Date(range.endDay).getTime() - new Date(range.startDay).getTime()) / 86400000) + 1;
        return days * branchIds.length;
    }, [range, branchIds]);

    const onDownload = () => {
        if (!current || !previous) return;
        const rows = STATEMENT_ROWS.map((row) => ({
            Concepto: `${signOf(row)} ${row.label}`.trim(),
            [periodLabel]: current[row.key] as number,
            '% de ingresos': current.income ? Number((((current[row.key] as number) || 0) / current.income * 100).toFixed(1)) : '',
            'Periodo anterior': previous[row.key] as number,
            Estimado: row.group ? current.estimatedByGroup[row.group] || 0 : ''
        }));
        const info = [
            { Concepto: 'Propinas cobradas (no son venta)', [periodLabel]: current.tipsCollected, 'Periodo anterior': previous.tipsCollected },
            { Concepto: 'Propinas entregadas (no son gasto)', [periodLabel]: current.tipsDelivered, 'Periodo anterior': previous.tipsDelivered },
            { Concepto: 'Ajustes de caja (no son gasto)', [periodLabel]: current.adjustments, 'Periodo anterior': previous.adjustments }
        ];
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet([...rows, {}, ...info]);
        ws['!cols'] = [{ wch: 44 }, { wch: 18 }, { wch: 14 }, { wch: 18 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Estado de resultados');
        if (byBranch) {
            const branchRows = STATEMENT_ROWS.map((row) => ({
                Concepto: `${signOf(row)} ${row.label}`.trim(),
                ...Object.fromEntries(branchIds.map((id) => [branchName(id), byBranch[id][row.key] as number]))
            }));
            const ws2 = XLSX.utils.json_to_sheet(branchRows);
            ws2['!cols'] = [{ wch: 44 }, ...branchIds.map(() => ({ wch: 16 }))];
            XLSX.utils.book_append_sheet(wb, ws2, 'Por sucursal');
        }
        XLSX.writeFile(wb, `Estado de resultados - ${periodLabel} - ${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
    };

    const valueCell = (statement: IncomeStatement | undefined, row: StatementRow) => {
        const value = (statement?.[row.key] as number) || 0;
        return row.kind === 'minus' && value ? `−${currencyFormatter(value)}` : currencyFormatter(value);
    };

    return (
        <Stack height="100%" minHeight={0} spacing={2} p={2} boxSizing="border-box" sx={{ overflowY: 'auto' }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap flexShrink={0}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="er-from">Desde</InputLabel>
                    <Select labelId="er-from" label="Desde" value={from} onChange={(event) => setFrom(event.target.value)}>
                        {monthOptions.map((month) => (
                            <MenuItem key={month} value={month}>
                                {formatPeriod(month)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="er-to">Hasta</InputLabel>
                    <Select labelId="er-to" label="Hasta" value={to} onChange={(event) => setTo(event.target.value)}>
                        {monthOptions.map((month) => (
                            <MenuItem key={month} value={month}>
                                {formatPeriod(month)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                    {consolidated
                        ? scope === 'all'
                            ? 'Consolidado de todas las sucursales'
                            : `Tus sucursales (${branchIds.length})`
                        : branchName(branchOfficeId)}
                </Typography>
                <Button variant="outlined" sx={{ ml: 'auto !important' }} onClick={onDownload} disabled={!current}>
                    Excel del estado
                </Button>
            </Stack>

            {range.empty ? (
                <Alert severity="info">El periodo todavía no tiene días cerrados: el día de hoy no entra.</Alert>
            ) : (
                <Alert severity="info" sx={{ flexShrink: 0 }}>
                    {`Del ${range.startDay.split('-').reverse().join('/')} al ${range.endDay.split('-').reverse().join('/')}${
                        range.partial ? ' (a día cerrado: hoy no entra)' : ''
                    }. Se compara contra ${
                        previousRange.startDay.split('-').reverse().join('/')
                    } – ${previousRange.endDay.split('-').reverse().join('/')}.`}
                </Alert>
            )}
            {current && !range.empty && current.salesDays < expectedDays && (
                <Alert severity="warning" sx={{ flexShrink: 0 }}>
                    {`Faltan las ventas de ${expectedDays - current.salesDays} día(s)-sucursal: el aseo las escribe cada mañana y el relleno histórico, una vez.`}
                </Alert>
            )}
            {current && current.estimated > 0 && (
                <Alert severity="warning" sx={{ flexShrink: 0 }}>
                    {`Incluye ${currencyFormatter(current.estimated)} estimados de gastos fijos que nadie ha confirmado (Gastos fijos → Por confirmar).`}
                </Alert>
            )}

            <TableContainer sx={{ flexShrink: 0 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Concepto</TableCell>
                            <TableCell align="right">{periodLabel}</TableCell>
                            <TableCell align="right">% de ingresos</TableCell>
                            <TableCell align="right">Periodo anterior</TableCell>
                            <TableCell align="right">Variación</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {STATEMENT_ROWS.map((row) => {
                            const value = (current?.[row.key] as number) || 0;
                            const before = (previous?.[row.key] as number) || 0;
                            const change = variation(value, before);
                            const estimated = row.group ? current?.estimatedByGroup[row.group] || 0 : 0;
                            const isResult = row.kind === 'result';
                            if (row.key === 'uncategorized' && !value && !before) return null;
                            return (
                                <TableRow key={row.key} sx={isResult ? { bgcolor: 'action.hover' } : undefined}>
                                    <TableCell sx={{ pl: row.indent ? 5 : 2, fontWeight: isResult ? 700 : 400, color: row.kind === 'info' ? 'text.secondary' : undefined }}>
                                        {`${signOf(row)} ${row.label}`.trim()}
                                        {estimated > 0 && (
                                            <Typography component="span" variant="caption" color="warning.main" ml={1}>
                                                {`incluye ${currencyFormatter(estimated)} estimado`}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: isResult ? 700 : 400, whiteSpace: 'nowrap' }}>
                                        {valueCell(current, row)}
                                        {row.margin && current && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {`margen ${percent(current[row.margin] as number | null)}`}
                                            </Typography>
                                        )}
                                        {row.share && current && (current[row.share] as number | null) !== null && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {`${percent(current[row.share] as number | null)} ${row.shareLabel || ''}`.trim()}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                        {current?.income && row.kind !== 'info' ? percent(value / current.income) : ''}
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                        {valueCell(previous, row)}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            color:
                                                change === null
                                                    ? 'text.secondary'
                                                    : (row.kind === 'minus' ? change <= 0 : change >= 0)
                                                    ? 'success.main'
                                                    : 'error.main'
                                        }}
                                    >
                                        {change === null ? '—' : `${change > 0 ? '+' : ''}${(change * 100).toFixed(1)}%`}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {current && (
                <Stack spacing={0.5} flexShrink={0}>
                    <Typography variant="subtitle2" fontWeight={600}>
                        Informativo (no suma ni resta)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {`Propinas cobradas: ${currencyFormatter(current.tipsCollected)} · entregadas en efectivo: ${currencyFormatter(
                            current.tipsDelivered
                        )} · ajustes de caja: ${currencyFormatter(current.adjustments)} · ${current.orders} pedidos completados`}
                    </Typography>
                </Stack>
            )}

            {byBranch && (
                <Stack spacing={1} flexShrink={0}>
                    <Typography variant="h6" fontWeight={600}>
                        Por sucursal
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sucursal</TableCell>
                                    <TableCell align="right">Ventas netas</TableCell>
                                    <TableCell align="right">Insumos</TableCell>
                                    <TableCell align="right">Utilidad bruta</TableCell>
                                    <TableCell align="right">Nómina</TableCell>
                                    <TableCell align="right">Otros gastos</TableCell>
                                    <TableCell align="right">Utilidad de operación</TableCell>
                                    <TableCell align="right">Margen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {branchIds
                                    .map((id) => ({ id, statement: byBranch[id] }))
                                    .sort((a, b) => b.statement.operatingProfit - a.statement.operatingProfit)
                                    .map(({ id, statement }) => (
                                        <TableRow key={id} hover>
                                            <TableCell>{branchName(id)}</TableCell>
                                            <TableCell align="right">{currencyFormatter(statement.netSales)}</TableCell>
                                            <TableCell align="right">{currencyFormatter(statement.supplies)}</TableCell>
                                            <TableCell align="right">{currencyFormatter(statement.grossProfit)}</TableCell>
                                            <TableCell align="right">{currencyFormatter(statement.payroll)}</TableCell>
                                            <TableCell align="right">
                                                {currencyFormatter(statement.operatingExpenses - statement.payroll)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ fontWeight: 700, color: statement.operatingProfit < 0 ? 'error.main' : undefined }}
                                            >
                                                {currencyFormatter(statement.operatingProfit)}
                                            </TableCell>
                                            <TableCell align="right">{percent(statement.operatingMargin)}</TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            )}
        </Stack>
    );
};

export default IncomeStatementPanel;
